import {Switch} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import FieldDefinitionLabel from 'registry/field/FieldDefinitionLabel';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Modal from 'ui-library/modals/Modal';

import useSimpleRules, {LegacySimpleRules, ValidationRuleModalContainerProps} from './useSimpleRules';

export interface ValidationRuleModalProps {
  fieldDefinitionLabel: JSX.Element;
  legacySimpleRules: LegacySimpleRules;
  handleUpdateLegacyRules: (legacySimpleRules: LegacySimpleRules) => void;
  onRequestSubmit: () => void;
  onRequestClose: () => void;
}

interface BaseValidationRuleModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  handleUpdateLegacyRules: (legacySimpleRules: LegacySimpleRules) => void;
  children?: React.ReactNode;
  disabled: boolean;
  legacySimpleRules: LegacySimpleRules;
  fieldDefinitionLabel: JSX.Element; // should be a FieldDefinitionLabel
  enableMultiSelectOption?: boolean;
  isMulti?: boolean;
  hideRestrictValuesHeader?: boolean;
  title?: string;
}

export default function BaseValidationRuleModal(props: BaseValidationRuleModalProps): JSX.Element {
  const otherOptions = Boolean(props.children) || (props.enableMultiSelectOption ?? false);

  return (
    <Modal
      title={props.title ?? 'Validation Rules'}
      onRequestSubmit={props.onRequestSubmit}
      onRequestClose={props.onRequestClose}
      buttons={[
        {
          submit: true,
          text: 'Save',
          style: ButtonStyle.PRIMARY,
          disabled: props.disabled,
        },
      ]}
      medium={true}
      dataTestAttrs={{component: 'BaseValidationRuleModal', element: 'root'}}
    >
      {BenchlingConfig.VALIDATION_RULES_ENGINE_ENABLE_M3 && (
        <div>
          <FormGroup
            disableBottomMargin={false}
            className={css(otherOptions && commonStyles.multipleFormGroup, commonStyles.headerFormGroup)}
          >
            <Row className={css(commonStyles.headerRow)}>
              <Label xs={12} sm={12} md={12} lg={12} className={css(commonStyles.headerLabel)}>
                Data submitted to this {props.fieldDefinitionLabel} field must follow these rules.
              </Label>
            </Row>
            <Row className={css(commonStyles.titleRow)}>
              <Switch
                classNames={{
                  body: css(commonStyles.switchBody),
                  label: css(commonStyles.switchLabel),
                  description: css(commonStyles.subtitleLabel),
                }}
                checked={props.legacySimpleRules.isRequired}
                onChange={(e) => {
                  props.handleUpdateLegacyRules({
                    ...props.legacySimpleRules,
                    isRequired: e.currentTarget.checked,
                  });
                }}
                size="md"
                labelPosition="left"
                label="Require value"
                description="Value must be filled to submit schema."
                {...getTestProps({component: 'BaseValidationRuleModal', element: 'isRequiredSwitch'})}
              />
            </Row>
          </FormGroup>
          {(props.enableMultiSelectOption ?? false) && (
            <FormGroup
              disableBottomMargin={false}
              className={css(
                Boolean(props.children) && commonStyles.multipleFormGroup,
                commonStyles.headerFormGroup
              )}
            >
              <Row className={css(commonStyles.titleRow)}>
                <Switch
                  classNames={{
                    body: css(commonStyles.switchBody),
                    label: css(commonStyles.switchLabel),
                    description: css(commonStyles.subtitleLabel),
                  }}
                  checked={props.legacySimpleRules.isMulti}
                  onChange={(e) => {
                    props.handleUpdateLegacyRules({
                      ...props.legacySimpleRules,
                      isMulti: e.currentTarget.checked,
                    });
                  }}
                  size="md"
                  labelPosition="left"
                  label="Allow multi-select"
                  description="Allow multiple values to be set."
                  {...getTestProps({component: 'BaseValidationRuleModal', element: 'isMultiSwitch'})}
                />
              </Row>
            </FormGroup>
          )}
        </div>
      )}
      {Boolean(props.children) && (
        <FormGroup disableBottomMargin={true} className={css(commonStyles.bodyFormGroup)}>
          {!(props.hideRestrictValuesHeader ?? false) && (
            <Row className={css(commonStyles.titleRow)}>
              <Label xs={12} sm={12} md={12} lg={12} className={css(commonStyles.titleLabel)}>
                Restrict values
              </Label>
              <Label xs={12} sm={12} md={12} lg={12} className={css(commonStyles.subtitleLabel)}>
                Limit what values can be set for this field.
              </Label>
            </Row>
          )}
          {props.children}
        </FormGroup>
      )}
    </Modal>
  );
}
export const commonStyles = StyleSheet.create({
  headerFormGroup: {
    fontSize: '13px',
  },
  multipleFormGroup: {
    borderBottom: `1px solid ${GRAY_LIGHT}`,
    marginBottom: '16px',
    paddingBottom: '16px',
  },
  headerRow: {
    marginBottom: '16px',
  },
  headerLabel: {
    fontWeight: 'normal',
    marginBottom: '0px',
  },
  bodyFormGroup: {
    fontSize: '13px',
    marginBottom: '16px',
  },
  titleRow: {
    marginTop: '0px',
    marginBottom: '0px',
  },
  titleLabel: {
    marginBottom: '0px',
  },
  subtitleLabel: {
    fontSize: '12px',
    color: 'grey',
    fontWeight: 'normal',
    marginBottom: '0px',
  },
  middleRow: {
    marginTop: '16px',
  },
  leftColumn: {
    paddingRight: '4px',
  },
  rightColumn: {
    paddingLeft: '4px',
  },
  switchBody: {
    padding: '0 15px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    marginBottom: 0, // Override benchling.css
  },
  switchLabel: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
});

export function BaseValidationRuleModalContainer(props: ValidationRuleModalContainerProps): JSX.Element {
  const [simpleRulesState, simpleRulesActions] = useSimpleRules(props.schemaField);
  const legacySimpleRules = simpleRulesState.legacySimpleRules;

  const fieldDefinitionLabel = (
    <Chip
      className={css(styles.fieldDefinitionLabel)}
      text={
        <FieldDefinitionLabel
          field={props.schemaField}
          schemaFieldSelectors={props.schemaFieldSelectors ?? []}
        />
      }
      size={ChipSize.MINI}
    />
  );

  return (
    <BaseValidationRuleModal
      legacySimpleRules={legacySimpleRules}
      onRequestSubmit={() => {
        props.onRequestSubmit(simpleRulesState);
      }}
      onRequestClose={props.onRequestClose}
      handleUpdateLegacyRules={simpleRulesActions.handleUpdateLegacySimpleRules}
      disabled={false}
      fieldDefinitionLabel={fieldDefinitionLabel}
      enableMultiSelectOption={props.enableMultiSelectOption}
    />
  );
}

const styles = StyleSheet.create({
  fieldDefinitionLabel: {
    marginRight: 0,
  },
});
